import { generateDoorBoard, getBoardSquare } from './doorBoard';
import { createEmptyPortalPair } from './portalPair';
import { commitChainReaction } from './chainReaction';
import { startThrowCombo } from './throwCombo';
import { getDie, isValidDieRoll } from './dice';
import { CORE_SECTOR } from './runConstants';

export const LAST_STAND_TARGET = 76;

export const getLastStandEncounter = (game) =>
  game.guardian?.active
    ? `guardian:${game.floor}:${game.guardian.round}`
    : game.floor === CORE_SECTOR
    ? `core:${game.bossRound}`
    : game.miniBoss?.active
    ? `warden:${game.miniBoss.round}`
    : `room:${game.floor}`;

export const canTriggerLastStand = (game) =>
  game.relics?.includes('lastStandProtocol') &&
  !game.lastStandProtocol?.used &&
  !game.lastStandProtocol?.pending;

export const didLastStandSurvive = (game) => {
  const attempt = game.lastStandProtocol?.lastAttempt;
  return (
    attempt?.encounter === getLastStandEncounter(game) &&
    attempt.success === true
  );
};

export const requestLastStandRoll = (
  game,
  context,
  damage,
  hpBefore,
  random = Math.random
) => {
  if (!canTriggerLastStand(game)) return game;
  return {
    ...game,
    lastStandProtocol: {
      ...game.lastStandProtocol,
      pending: {
        encounter: getLastStandEncounter(game),
        context,
        damage,
        hpBefore,
        stage: 'ready',
        selectedDie: getDie(game.selectedDie).id,
        // Survival checks use the standard numeric and critical tiles. Neutral
        // tiles occupy the usual utility slots, preserving their roll odds.
        doorBoard: generateDoorBoard(random).map((square) =>
          !square.effect ||
          ['critical', 'criticalMiss'].includes(square.effect.type)
            ? square
            : { modifier: 0 }
        ),
        roll: null,
        landingIndex: null,
        boardModifier: 0,
        flipped: false,
        launch: null,
        impactSeen: false,
        rescueSeen: false,
        portalPair: createEmptyPortalPair(),
        chainReactionActivation: null,
        throwCombo: null,
      },
    },
  };
};

export const getLastStandRollGame = (game) => {
  const pending = game.lastStandProtocol?.pending;
  if (!pending) return null;
  return {
    ...game,
    phase: pending.stage === 'ready' ? 'ready' : 'rolled',
    selectedDie: getDie(pending.selectedDie || game.selectedDie).id,
    roll: pending.roll,
    landingIndex: pending.landingIndex,
    doorBoard: pending.doorBoard,
    boardModifier: pending.boardModifier,
    flipped: pending.flipped,
    boardTreasureReward: null,
    boardTreasureRewards: [],
    boardTileResolution: null,
    powerUp: null,
    chainReactionActivation: pending.chainReactionActivation || null,
    throwCombo: pending.throwCombo || null,
    bossRoundState: null,
    portalPair: {
      ...game.portalPair,
      ...(pending.portalPair || { entryIndex: null, exitIndex: null }),
    },
  };
};

export const beginLastStandRoll = (game, launch) => {
  const pending = game.lastStandProtocol?.pending;
  if (pending?.stage !== 'ready') return game;
  return {
    ...game,
    lastStandProtocol: {
      ...game.lastStandProtocol,
      pending: {
        ...pending,
        stage: 'rolling',
        launch,
        throwCombo: startThrowCombo(getLastStandRollGame(game)).throwCombo,
      },
    },
  };
};

export const commitLastStandRoll = (game, roll, landingIndex) => {
  const pending = game.lastStandProtocol?.pending;
  const landing = getBoardSquare(pending?.doorBoard, landingIndex);
  if (
    pending?.stage !== 'rolling' ||
    !landing ||
    !isValidDieRoll(roll, pending.selectedDie || game.selectedDie)
  )
    return game;
  const settled = commitChainReaction(getLastStandRollGame(game), landingIndex);
  return {
    ...game,
    chainReaction: settled.chainReaction,
    phaseAnchor: { ...game.phaseAnchor, armed: false },
    lastStandProtocol: {
      ...game.lastStandProtocol,
      pending: {
        ...pending,
        stage: 'settled',
        roll,
        landingIndex,
        boardModifier: landing.modifier || 0,
        portalPair: settled.portalPair,
        chainReactionActivation: settled.chainReactionActivation,
        throwCombo: settled.throwCombo,
      },
    },
  };
};

// Share relic charges with the main throw while keeping the survival throw's
// die face and portal pair separate.
export const updateLastStandRoll = (game, update) => {
  const rollGame = getLastStandRollGame(game);
  if (!rollGame) return game;
  const updated = update(rollGame);
  if (updated === rollGame) return game;
  return {
    ...game,
    floorFlipUsed: updated.floorFlipUsed,
    phaseAnchor: updated.phaseAnchor,
    lastStandProtocol: {
      ...game.lastStandProtocol,
      pending: {
        ...game.lastStandProtocol.pending,
        selectedDie: updated.selectedDie,
        flipped: updated.flipped,
        throwCombo: updated.throwCombo,
        portalPair: updated.portalPair,
      },
    },
  };
};
